<template>
  <div
    class="dui-radio-group"
    :class="vm.colorClass"
  >
    <slot name="options" />

    <div
      v-for="option in vm.options"
      :key="option.idx"
      class="input-radio-block"
      :title="option.text"
    >
      <input
        type="radio"
        :checked="(option.value === vm.value)"
        :name="name"
        :value="option.value"
        @change="optionChanged"
      >
      <span class="radio" />
      <label :class="{ 'alt-font': altFont }">{{ option.text }}</label>
    </div>
  </div>
</template>

<script>
import coloredComponent from '@dui/mixins/colored-component';
import storeKey from '@dui/mixins/store-key';

export default {
  mixins: [
    coloredComponent,
    storeKey,
  ],
  props: {
    altFont: {
      type: Boolean,
      default: false,
    },
    name: {
      default: null,
      type: String,
    },
    options: {
      type: Array,
      default: () => null,
    },
    value: {
      default: null,
      type: String,
    },
  },
  data: function () {
    return {
      vm: {
        options: [],
        value: this.value,
      },
    };
  },
  computed: {
    storeOptions() {
      return this.loadFromStore();
    },
  },
  watch: {
    options: function() {
      this.loadInitialOptions();
    },
    storeOptions: function() {
      this.loadInitialOptions();
    },
    value: function(newVal) {
      this.vm.value = newVal;
    },
  },
  mounted() {
    this.loadInitialOptions();
  },
  methods: {
    getOptions() {
      if (this.storeLoadKey) {
        return this.storeOptions;
      }

      if (this.options) {
        return this.options;
      }

      const elementOptions = this.$el.querySelectorAll('.dui-option');
      let idx = 0;
      const options = [];
      [].forEach.call(elementOptions, (elementOption) => {
        const option = {
          idx: idx,
          text: elementOption.dataset.text,
          value: elementOption.dataset.value,
        };

        elementOption.remove();
        options.push(option);
        idx++;
      });

      return options;
    },

    loadInitialOptions() {
      const options = this.getOptions();
      this.$set(this.vm, 'options', options);
    },

    optionChanged(ev) {
      const option = ev.target;
      if (!option.checked) {
        return;
      }

      this.vm.value = option.value;
      this.$emit('input', option.value);
      this.$emit('change');

      if (!this.storeUpdateKey) {
        return;
      }

      this.$store.dispatch(this.storeUpdateKey, option);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@dui-styles/base";

.dui-radio-group {
  box-sizing: border-box;
  display: inline-flex;
  flex-direction: column;
  width: 100%;

  @include makeColorRadio($yellow);

  &[disabled] {
    opacity: $disabled-opacity;
    pointer-events: none;
  }

  .input-radio-block {
    margin-bottom: $_1em;
    position: relative;

    &:hover input[type="radio"]:not(:checked) + span.radio:after {
      height: $_1em;
      width: $_1em;
    }

    input[type="radio"] {
      cursor: pointer;
      display: inline-block;
      height: 100%;
      left: 0;
      opacity: 0;
      position: absolute;
      width: 100%;
    }

    span.radio,
    label {
      display: inline-block;
      vertical-align: middle;
    }

    span.radio {
      height: $_3em;
      left: $border-width-base;
      pointer-events: none;
      position: absolute;
      top: $border-width-base;
      width: $_3em;

      &:before,
      &:after {
        border-radius: 50%;
        content: " ";
        left: 50%;
        position: absolute;
        top: 50%;
        transform: translate3d(-50%, -50%, 0);

        @include transition((
          border-color,
          background-color,
          height,
          width
        ));
      }

      &:before {
        border: $border-width-base solid getColor($grey, fade);
        height: 100%;
        width: 100%;
      }

      &:after {
        height: 0;
        width: 0;
      }
    }

    label {
      font-weight: normal;
      font-size: $font-size-base;
      margin: 0;
      padding-left: ($_5em - $border-width-base);

      &.alt-font {
        font-family: $alt-font;
        font-size: $font-size-lg;
      }
    }
  }

  // Color variants
  &.color-yellow { @include makeColorRadio($yellow); }
  &.color-black  { @include makeColorRadio($black); }
  &.color-grey   { @include makeColorRadio($grey, dark); }
  &.color-red    { @include makeColorRadio($red); }
  &.color-sand   { @include makeColorRadio($sand, dark); }
  &.color-white  { @include makeColorRadio($white); }
  // END Color variants
}
</style>
